﻿import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page, Card, Button, Banner, Badge, Text, Select,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, Box, Pagination, InlineGrid, ProgressBar,
} from '@shopify/polaris';
import { CartUpIcon, CartIcon, RefreshIcon, ListBulletedIcon } from '@shopify/polaris-icons';
import { useUpsell } from '../../hooks/useUpsell';
import type { ProductPair, UpsellRecommendation, UpsellRecommendationItem, UpsellRecommendationType } from '../../api/upsell';
import { StatCard } from '../shared/StatCard';
import { IconBadge } from '../shared/IconBadge';

/**
 * AI recommendation items only carry product *titles* (the AI model doesn't
 * see Shopify IDs), while the same recommendation's `product_pairs` carry
 * both title and real shopify_id. Cross-referencing lets a "bundle" card
 * offer a one-click path into Bundle Discounts without the merchant retyping
 * product IDs — falls back to no button if the AI paraphrased a title enough
 * that it no longer matches any computed pair (safer than sending wrong IDs).
 */
function resolveBundleProductIds(rec: UpsellRecommendationItem, pairs: ProductPair[]): { productAId: number; productBId: number } | null {
  if (rec.products.length !== 2) return null;
  const [titleA, titleB] = rec.products;
  const match = pairs.find((p) =>
    (p.product_a_title === titleA && p.product_b_title === titleB) ||
    (p.product_a_title === titleB && p.product_b_title === titleA)
  );
  return match ? { productAId: match.product_a_id, productBId: match.product_b_id } : null;
}

function typeBadge(type: UpsellRecommendationType) {
  const map: Record<UpsellRecommendationType, { label: string; tone: 'success' | 'info' | 'attention' }> = {
    bundle:                { label: 'Bundle',              tone: 'success' },
    cross_sell:             { label: 'Cross-sell',          tone: 'info' },
    post_purchase_upsell:   { label: 'Post-purchase upsell', tone: 'attention' },
  };
  const { label, tone } = map[type] ?? { label: type, tone: 'info' as const };
  return <Badge tone={tone}>{label}</Badge>;
}

interface DetailProps {
  recommendation: UpsellRecommendation;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function RecommendationDetail({ recommendation, onBack, onDelete }: DetailProps) {
  const navigate = useNavigate();

  return (
    <Page fullWidth
      title={`Upsell Recommendations: ${recommendation.period_start} – ${recommendation.period_end}`}
      backAction={{ content: 'All Recommendations', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(recommendation.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">Orders analysed</Text>
                <Text as="p" variant="heading2xl" fontWeight="bold">{recommendation.orders_analyzed}</Text>
              </BlockStack>
            </Card>

            {recommendation.ai_summary && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Summary</Text>
                  <Text as="p" variant="bodyMd">{recommendation.ai_summary}</Text>
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Recommendations</Text>
                {recommendation.ai_recommendations.length === 0 ? (
                  <Text as="p" tone="subdued">No recommendations generated for this period.</Text>
                ) : (
                  <BlockStack gap="300">
                    {recommendation.ai_recommendations.map((r, i) => {
                      const bundleIds = r.type === 'bundle' ? resolveBundleProductIds(r, recommendation.product_pairs) : null;

                      return (
                        <Card key={i} background="bg-surface-secondary">
                          <BlockStack gap="150">
                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                              <IconBadge icon={CartUpIcon} color={r.type === 'bundle' ? 'green' : r.type === 'cross_sell' ? 'blue' : 'orange'} />
                              <BlockStack gap="050">
                                <Text as="h3" variant="headingSm">{r.title}</Text>
                                {typeBadge(r.type)}
                              </BlockStack>
                            </InlineStack>
                            <Text as="p" variant="bodySm" tone="subdued">
                              Products: {r.products.join(', ')}
                            </Text>
                            <Text as="p" variant="bodyMd">{r.rationale}</Text>
                            <Text as="p" variant="bodySm" tone="success">
                              Expected impact: {r.expected_impact}
                            </Text>
                            {r.type === 'bundle' && r.regular_price != null && r.bundle_price != null && (
                              <InlineStack gap="200" blockAlign="center">
                                <Badge tone="attention">{`${r.suggested_discount_percent}% off`}</Badge>
                                <Text as="span" variant="bodySm" tone="subdued">
                                  <s>${r.regular_price.toFixed(2)}</s>
                                </Text>
                                <Text as="span" variant="bodySm" fontWeight="semibold">
                                  ${r.bundle_price.toFixed(2)} bundle price
                                </Text>
                              </InlineStack>
                            )}
                            {bundleIds && (
                              <Box>
                                <Button
                                  size="slim"
                                  onClick={() => navigate('/bundles', {
                                    state: {
                                      prefill: {
                                        productIds: [String(bundleIds.productAId), String(bundleIds.productBId)],
                                        discountPercent: String(r.suggested_discount_percent ?? 15),
                                        title: r.title,
                                      },
                                    },
                                  })}
                                >
                                  Create Bundle Discount
                                </Button>
                              </Box>
                            )}
                          </BlockStack>
                        </Card>
                      );
                    })}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Frequently Bought Together</Text>
                {recommendation.product_pairs.length === 0 ? (
                  <Text as="p" tone="subdued">No repeat product pairs found for this period.</Text>
                ) : (
                  <BlockStack gap="200">
                    {recommendation.product_pairs.map((p, i) => {
                      const attachPct = Math.round(p.attach_rate * 100);
                      return (
                        <Card key={i} background="bg-surface-secondary">
                          <BlockStack gap="150">
                            <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                              <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {p.product_a_title} + {p.product_b_title}
                              </Text>
                              <Badge tone="info">{`${p.co_occurrence} orders`}</Badge>
                            </InlineStack>
                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                              <Box width="100%">
                                <ProgressBar progress={attachPct} size="small" tone="primary" />
                              </Box>
                              <Text as="span" variant="bodySm" tone="subdued">{`${attachPct}% attach rate`}</Text>
                            </InlineStack>
                            {(p.product_a_price != null || p.product_b_price != null) && (
                              <Text as="span" variant="bodySm" tone="subdued">
                                {p.product_a_price != null ? `$${p.product_a_price.toFixed(2)}` : 'price unknown'}
                                {' + '}
                                {p.product_b_price != null ? `$${p.product_b_price.toFixed(2)}` : 'price unknown'}
                              </Text>
                            )}
                          </BlockStack>
                        </Card>
                      );
                    })}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

const PERIOD_OPTIONS = [
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last 180 days', value: '180' },
];

export function UpsellPanel() {
  const {
    recommendations, activeRecommendation, loading, generating, error,
    total, page, lastPage, fetchRecommendations, generate, deleteRecommendation, setActiveRecommendation, setError,
    widgetStatus, refreshingWidget, fetchWidgetStatus, refreshWidget,
  } = useUpsell();

  const [days, setDays] = useState('90');

  useEffect(() => {
    fetchRecommendations(1);
    fetchWidgetStatus();
  }, []);

  if (activeRecommendation) {
    return (
      <RecommendationDetail
        recommendation={activeRecommendation}
        onBack={() => setActiveRecommendation(null)}
        onDelete={deleteRecommendation}
      />
    );
  }

  return (
    <Page fullWidth
      title="Upsell & Cross-Sell Recommendations"
      subtitle="AI-powered bundle, cross-sell, and post-purchase upsell ideas based on what your customers actually buy together"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="300">
            <StatCard
              label="Product Relationships Live"
              value={String(widgetStatus?.pairs_count ?? 0)}
              icon={CartUpIcon}
              iconColor="purple"
              deltaLabel={widgetStatus?.last_refreshed_at ? `Refreshed ${new Date(widgetStatus.last_refreshed_at).toLocaleDateString()}` : 'Not yet populated'}
            />
            <StatCard
              label="Storefront Add-to-Carts"
              value={String(widgetStatus?.total_add_to_carts ?? 0)}
              icon={CartIcon}
              iconColor="green"
              deltaLabel="From the live product-page widget"
            />
            <StatCard
              label="Past Recommendation Runs"
              value={String(total)}
              icon={ListBulletedIcon}
              iconColor="indigo"
            />
            <StatCard
              label="Latest Run Recommendations"
              value={String(recommendations[0]?.ai_recommendations.length ?? 0)}
              icon={RefreshIcon}
              iconColor="teal"
              deltaLabel={recommendations[0] ? `${recommendations[0].orders_analyzed} orders analysed` : undefined}
            />
          </InlineGrid>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Storefront Widget</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Shows a live "Frequently Bought Together" section on your product pages, with real Add to Cart
                buttons, a "% of customers buy this together" trust signal, and variant/quantity selection.
                This is pure math from your order history &mdash; no AI cost to refresh, so refresh it as
                often as you like.
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                If a pair shown here already has a matching bundle in <strong>Bundle Discounts</strong>, the
                storefront card automatically upgrades to "Add both &amp; save X%" and applies that real discount
                code at checkout &mdash; no extra setup needed beyond creating the bundle.
              </Text>
              <Box>
                <Button loading={refreshingWidget} onClick={() => refreshWidget({ days: Number(days) })}>
                  Refresh Storefront Widget
                </Button>
              </Box>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Generate New Recommendations</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Analyses your recent order history to find products frequently bought together, then generates
                AI-powered bundle, cross-sell widget, and post-purchase upsell suggestions.
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="200px">
                  <Select label="Period" options={PERIOD_OPTIONS} value={days} onChange={setDays} />
                </Box>
                <Button
                  variant="primary"
                  loading={generating}
                  onClick={() => generate({ days: Number(days) })}
                >
                  Generate Recommendations
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Past Recommendations ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : recommendations.length === 0 ? (
                <EmptyState
                  heading="No upsell recommendations yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Generate First Recommendations', onAction: () => generate({ days: Number(days) }) }}
                >
                  <p>Generate recommendations to find bundle and cross-sell opportunities from your order history.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                  headings={['Period', 'Orders Analysed', 'Recommendations', 'Actions']}
                  rows={recommendations.map((r) => [
                    <Button variant="plain" onClick={() => setActiveRecommendation(r)}>{`${r.period_start} – ${r.period_end}`}</Button>,
                    String(r.orders_analyzed),
                    String(r.ai_recommendations.length),
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteRecommendation(r.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchRecommendations(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchRecommendations(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">One more step: add it to your product pages</Text>
              <Text as="p" tone="subdued">
                Go to <strong>Online Store &rarr; Themes &rarr; Customize</strong>, open a product page, and use{' '}
                <strong>Add block &rarr; Apps &rarr; Frequently Bought Together</strong>. This is a one-time setup
                per theme &mdash; refreshing the widget above keeps it up to date automatically.
              </Text>
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
